<template>
  <el-dialog
    class="fault-batch-reassign-dialog"
    title="批量改派故障单"
    :visible.sync="dialogVisible"
    width="1200px"
    top="5vh"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <div class="batch-reassign-dialog">
      <div class="search-bar">
        <el-input
          v-model="queryForm.keyword"
          placeholder="故障编号/设备名称"
          clearable
          style="width: 190px"
          @keyup.enter.native="handleSearch"
        />
        <AreaCascader
          v-model="queryForm.areaId"
          :options="areaTree"
          placeholder="所属区域"
          clearable
          style="width: 190px"
          @change="handleSearch"
        />
        <el-select
          v-model="queryForm.status"
          placeholder="处理状态"
          clearable
          style="width: 140px"
          @change="handleSearch"
        >
          <el-option label="待处理" value="pending" />
          <el-option label="已上报待处理" value="reported" />
        </el-select>
        <el-select
          v-model="queryForm.assignStatus"
          placeholder="是否已分配"
          clearable
          style="width: 140px"
          @change="handleSearch"
        >
          <el-option label="已分配" value="assigned" />
          <el-option label="未分配" value="unassigned" />
        </el-select>
        <el-date-picker
          v-model="foundDateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="发现时间起"
          end-placeholder="发现时间止"
          value-format="yyyy-MM-dd HH:mm:ss"
          style="width: 260px"
          @change="handleFoundDateChange"
        />
        <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">搜索</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <div class="selection-toolbar">
        <el-checkbox
          v-model="selectAllMatching"
          :indeterminate="selectAllIndeterminate"
          :disabled="!total || loading"
          @change="handleSelectAllChange"
        >
          全选当前筛选结果（{{ total }} 条）
        </el-checkbox>
        <span class="selected-count">已选 {{ selectedCount }} 条</span>
        <span v-if="selectAllMatching" class="selection-hint">
          当前筛选结果全部纳入改派，可取消勾选个别故障单
        </span>
      </div>

      <el-table
        ref="faultTable"
        v-loading="loading"
        :data="tableData"
        row-key="uuid"
        border
        stripe
        height="280"
        :header-cell-style="{ textAlign: 'center' }"
        :cell-style="{ textAlign: 'center' }"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="25" />
        <el-table-column prop="faultCode" label="故障编号" width="130" />
        <el-table-column label="设备信息" min-width="190">
          <template slot-scope="{ row }">
            <div class="device-name">{{ row.deviceName || '-' }}</div>
            <div class="device-area">{{ row.areaPath || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="faultDesc" label="故障描述" min-width="190" show-overflow-tooltip />
        <el-table-column label="故障级别" width="60">
          <template slot-scope="{ row }">
            <el-tag :type="getFaultLevelType(row.faultLevel)" size="small">
              {{ row.faultLevelName || row.faultLevel || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="处理状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ row.statusName || row.status || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="分配信息" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span v-if="!row.assignType || row.assignType === 'none'" class="text-muted">
              未指定
            </span>
            <template v-else-if="row.assignType === 'team'">
              <el-tag type="warning" size="mini" effect="plain">
                {{ row.assignTeamName || '指定小组' }}
              </el-tag>
              <div v-if="row.teamAssigneeName" class="assign-delegate-text">
                组内委派：{{ row.teamAssigneeName }}
              </div>
            </template>
            <span v-else>{{ row.assignUserNames || row.assignUserIds || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="发现时间" width="130">
          <template slot-scope="{ row }">
            {{ formatDateTime(row.foundTime) }}
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        :current-page="queryForm.pageNo"
        :page-sizes="[10, 20, 50]"
        :page-size="queryForm.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />

      <div class="assign-panel">
        <div class="assign-panel-title">
          统一分配信息
          <span class="assign-panel-hint">
            <span class="assign-panel-hint-label">提示：</span>
            <span>改派后原小组委派记录将清空</span>
          </span>
        </div>
        <el-form :model="assignForm" label-width="100px">
          <AssignTypeFields
            :model="assignForm"
            :allow-none="false"
            team-type="fault"
            preset="fault"
            label-width="100px"
            :active="visible"
            @navigate-to-team="handleNavigateToTeam"
            @navigate-to-org-user="handleNavigateToOrgUser"
          />
        </el-form>
      </div>
    </div>

    <div slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" :disabled="!selectedCount" @click="handleSubmit">
        确认改派（{{ selectedCount }}）
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { faultService } from './fault.service';
import { areaService } from '@/features/gnmk/area/area.service';
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import AssignTypeFields from '@/features/gnmk/components/AssignTypeFields.vue';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';

export default {
  name: 'FaultBatchReassignDialog',
  components: { AssignTypeFields, AreaCascader },
  props: {
    visible: { type: Boolean, default: false }
  },
  data () {
    return {
      loading: false,
      submitting: false,
      tableData: [],
      total: 0,
      areaTree: [],
      queryForm: this.createQueryForm(),
      foundDateRange: null,
      assignForm: this.createAssignForm(),
      selectedIds: {},
      excludedIds: {},
      selectAllMatching: false,
      restoringSelection: false
    };
  },
  computed: {
    dialogVisible: {
      get () { return this.visible; },
      set (value) { this.$emit('update:visible', value); }
    },
    selectedCount () {
      const ids = this.selectAllMatching ? this.excludedIds : this.selectedIds;
      const count = Object.keys(ids).length;
      return this.selectAllMatching ? Math.max(this.total - count, 0) : count;
    },
    selectAllIndeterminate () {
      return !this.selectAllMatching && this.selectedCount > 0 && this.selectedCount < this.total;
    }
  },
  methods: {
    createQueryForm () {
      return {
        keyword: '',
        areaId: '',
        status: '',
        assignStatus: '',
        foundTimeStart: null,
        foundTimeEnd: null,
        pageNo: 1,
        pageSize: 10
      };
    },
    createAssignForm () {
      return { assignType: '', assignUserIds: [], assignTeamId: null };
    },
    handleOpen () {
      this.queryForm = this.createQueryForm();
      this.foundDateRange = null;
      this.assignForm = this.createAssignForm();
      this.clearSelectionState();
      this.loadAreaTree();
      this.loadData();
    },
    handleClose () {
      this.clearSelectionState();
      this.tableData = [];
      this.total = 0;
    },
    loadAreaTree () {
      areaService.getAreaTree().then(res => {
        this.areaTree = res.data || [];
      });
    },
    buildQueryParams () {
      const params = { ...this.queryForm };
      if (!params.status) {
        params.statusList = ['pending', 'reported'];
      }
      return params;
    },
    loadData () {
      this.loading = true;
      faultService.queryFaultPage(this.buildQueryParams()).then(res => {
        const data = res.data || {};
        // 数据分页切换会触发表格清空选中事件，先屏蔽该事件，避免误删跨页选择。
        this.restoringSelection = true;
        this.tableData = data.list || data.rows || [];
        this.total = data.total || 0;
        this.restoreCurrentPageSelection();
      }).finally(() => {
        this.loading = false;
      });
    },
    handleSearch () {
      this.queryForm.pageNo = 1;
      this.clearSelectionState();
      this.loadData();
    },
    handleReset () {
      this.queryForm = this.createQueryForm();
      this.foundDateRange = null;
      this.clearSelectionState();
      this.loadData();
    },
    handleFoundDateChange (val) {
      if (val) {
        this.queryForm.foundTimeStart = val[0];
        this.queryForm.foundTimeEnd = val[1];
      } else {
        this.queryForm.foundTimeStart = null;
        this.queryForm.foundTimeEnd = null;
      }
      this.handleSearch();
    },
    handleSizeChange (pageSize) {
      this.queryForm.pageSize = pageSize;
      this.queryForm.pageNo = 1;
      this.restoreCurrentPageSelection();
      this.loadData();
    },
    handlePageChange (pageNo) {
      this.queryForm.pageNo = pageNo;
      this.loadData();
    },
    handleSelectAllChange (checked) {
      this.selectedIds = {};
      this.excludedIds = {};
      if (!checked) {
        this.clearTableSelection();
      }
      this.restoreCurrentPageSelection();
    },
    handleSelectionChange (rows) {
      if (this.restoringSelection) return;
      const currentPageIds = this.tableData.map(row => row.uuid);
      const currentSelectedIds = rows.map(row => row.uuid);
      if (this.selectAllMatching) {
        currentPageIds.forEach(id => {
          if (currentSelectedIds.includes(id)) {
            this.$delete(this.excludedIds, id);
          } else {
            this.$set(this.excludedIds, id, true);
          }
        });
      } else {
        currentPageIds.forEach(id => {
          if (currentSelectedIds.includes(id)) {
            this.$set(this.selectedIds, id, true);
          } else {
            this.$delete(this.selectedIds, id);
          }
        });
      }
    },
    restoreCurrentPageSelection () {
      this.$nextTick(() => {
        if (!this.$refs.faultTable) {
          this.restoringSelection = false;
          return;
        }
        this.restoringSelection = true;
        this.clearTableSelection();
        this.tableData.forEach(row => {
          const shouldSelect = this.selectAllMatching
            ? !this.excludedIds[row.uuid]
            : !!this.selectedIds[row.uuid];
          if (shouldSelect) this.$refs.faultTable.toggleRowSelection(row, true);
        });
        this.$nextTick(() => {
          this.restoringSelection = false;
        });
      });
    },
    clearTableSelection () {
      if (this.$refs.faultTable) this.$refs.faultTable.clearSelection();
    },
    clearSelectionState () {
      this.selectedIds = {};
      this.excludedIds = {};
      this.selectAllMatching = false;
      this.clearTableSelection();
    },
    validateSubmit () {
      if (!this.selectedCount) {
        this.$message.warning('请至少选择一条故障单');
        return false;
      }
      if (this.assignForm.assignType === 'user' && !this.assignForm.assignUserIds.length) {
        this.$message.warning('请选择至少一名执行人员');
        return false;
      }
      if (this.assignForm.assignType === 'team' && !this.assignForm.assignTeamId) {
        this.$message.warning('请选择执行小组');
        return false;
      }
      if (!['user', 'team'].includes(this.assignForm.assignType)) {
        this.$message.warning('请选择分配方式');
        return false;
      }
      return true;
    },
    buildSubmitData () {
      const form = this.assignForm;
      return {
        selectAll: this.selectAllMatching,
        faultIds: this.selectAllMatching ? [] : Object.keys(this.selectedIds),
        excludeFaultIds: this.selectAllMatching ? Object.keys(this.excludedIds) : [],
        query: this.selectAllMatching ? this.buildQueryParams() : null,
        assignType: form.assignType,
        assignUserIds: form.assignType === 'user' ? form.assignUserIds.join(',') : '',
        assignTeamId: form.assignType === 'team' ? form.assignTeamId : ''
      };
    },
    handleSubmit () {
      if (!this.validateSubmit()) return;
      this.$confirm(
        `确定将选中的 ${this.selectedCount} 条故障单统一改派吗？改派后原小组委派记录将清空。`,
        '确认批量改派',
        { type: 'warning', closeOnClickModal: false }
      ).then(() => {
        this.submitting = true;
        return faultService.batchReassignFault(this.buildSubmitData());
      }).then(() => {
        this.$message.success(`成功改派 ${this.selectedCount} 条故障单`);
        this.dialogVisible = false;
        this.$emit('success');
      }).finally(() => {
        this.submitting = false;
      });
    },
    handleNavigateToTeam () {
      this.dialogVisible = false;
      this.$router.push({ name: 'teamList' }).catch(() => {});
    },
    handleNavigateToOrgUser () {
      this.dialogVisible = false;
      this.$router.push({ name: 'orgUserList' }).catch(() => {});
    },
    getFaultLevelType (level) {
      return { urgent: 'danger', normal: 'warning', resolved: 'success' }[level] || '';
    },
    getStatusType (status) {
      return { pending: 'warning', reported: 'primary' }[status] || '';
    },
    formatDateTime
  },
  mounted () {
    if (this.visible) this.handleOpen();
  }
};
</script>

<style lang="scss" scoped>
.fault-batch-reassign-dialog {
  ::v-deep .el-dialog__body {
    padding-top: 14px;
    padding-bottom: 8px;
    overflow: visible;
  }
}

.batch-reassign-dialog {
  .search-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    padding: 10px;
    background: #f5f7fa;
    border-radius: 4px;
  }

  .selection-toolbar {
    display: flex;
    align-items: center;
    min-height: 36px;
    gap: 18px;
    color: #606266;
    font-size: 13px;
  }

  .selected-count {
    color: #409eff;
    font-weight: 500;
  }

  .selection-hint {
    color: #e6a23c;
  }

  .device-name {
    font-weight: 500;
  }

  .device-area {
    margin-top: 2px;
    color: #909399;
    font-size: 12px;
  }

  .text-muted {
    color: #c0c4cc;
  }

  .assign-delegate-text {
    margin-top: 3px;
    color: #409eff;
    font-size: 12px;
  }

  .pagination {
    margin-top: 12px;
    text-align: right;
  }

  .assign-panel {
    margin-top: 16px;
    padding: 12px 16px 2px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fafafa;
  }

  .assign-panel-title {
    margin-bottom: 10px;
    color: #303133;
    font-size: 14px;
    font-weight: 600;
  }

  .assign-panel-hint {
    margin-left: 12px;
    color: #e6a23c;
    font-size: 13px;
    font-weight: normal;
  }

  .assign-panel-hint-label {
    margin-right: 4px;
    color: #606266;
  }
}
</style>
